import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';

/**
 * A service provider's own OAuth applications.
 *
 * <p>The secret is never readable back — the API reports only whether one is
 * stored — so every form here loads with an empty secret field. See the note on
 * `save` for what that means.
 */

export type SocialProviderName = 'GOOGLE' | 'MICROSOFT' | 'OPENAI';

export interface SocialCredentialRow {
  provider: SocialProviderName;
  display_name: string;
  configured: boolean;
  client_id: string;
  /** Presence, never the value. */
  client_secret_set: boolean;
  enabled: boolean;
  updated_at: string | null;
}

export interface SocialCredentialSettings {
  /** The URL the reseller must register against their own application. */
  redirect_uri: string;
  providers: SocialCredentialRow[];
}

export const socialCredentialsService = {
  async list(): Promise<SocialCredentialSettings> {
    const response = await axiosInstance.get<ApiResponse<SocialCredentialSettings>>(
      '/v1/social-credentials',
    );
    return response.data?.data ?? { redirect_uri: '', providers: [] };
  },

  /**
   * Create or update one provider's application.
   *
   * <p><b>Omit `client_secret` to keep the stored one.</b> The field cannot be
   * populated from the server, so sending an empty string on every save would erase a
   * working credential each time somebody changed the client id or flipped the
   * switch. The caller sends it only when the user actually typed one.
   */
  async save(
    provider: SocialProviderName,
    body: { client_id: string; client_secret?: string; enabled?: boolean },
  ): Promise<ApiResponse<unknown>> {
    const response = await axiosInstance.put<ApiResponse<unknown>>(
      `/v1/social-credentials/${provider}`,
      body,
    );
    return response.data;
  },

  async remove(provider: SocialProviderName): Promise<ApiResponse<unknown>> {
    const response = await axiosInstance.delete<ApiResponse<unknown>>(
      `/v1/social-credentials/${provider}`,
    );
    return response.data;
  },
};

export default socialCredentialsService;
